import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import Icon from '../common/Icon';
import Logo from '../common/Logo';
import Button from '../common/Button';
import { useAuth } from '../../context/AuthContext';
import useNotificationCount from '../../hooks/useNotificationCount';
import geminiLogo from '../../Gemini_Generated_Image_rvsyaprvsyaprvsy.png';

const SidebarItem = ({ to, icon, label, badge, exact = false, largeFont = false }) => {
  const location = useLocation();
  const isActive = exact 
    ? location.pathname === to 
    : location.pathname.startsWith(to);

  return (
    <NavLink
      to={to}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 16px',
        color: isActive ? '#667eea' : '#64748b',
        textDecoration: 'none',
        transition: 'all 0.2s',
        borderRadius: '8px',
        margin: '2px 0',
        fontSize: largeFont ? '16px' : '14px',
        fontWeight: '600',
        background: isActive ? 'linear-gradient(135deg, #667eea, #764ba2)' : 'transparent',
        color: isActive ? '#ffffff' : '#64748b'
      }}
      onMouseEnter={(e) => {
        if (!isActive) {
          e.currentTarget.style.background = '#f1f5f9';
          e.currentTarget.style.color = '#475569';
        }
      }}
      onMouseLeave={(e) => {
        if (!isActive) {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.color = '#64748b';
        }
      }}
    >
      <Icon name={icon} size={18} />
      <span>{label}</span>
      {badge && (
        <span style={{
          fontSize: '10px',
          padding: '2px 6px',
          marginLeft: 'auto',
          background: '#ef4444',
          color: 'white',
          borderRadius: '4px',
          fontWeight: '700'
        }}>
          {badge}
        </span>
      )}
    </NavLink>
  );
};

const Sidebar = ({ isOpen, onClose, variant = 'default', isDesktop = false }) => {
  const { user, hasRole, logout } = useAuth();
  const { unreadCount } = useNotificationCount();

  const notificationBadge = unreadCount > 0 ? (unreadCount > 99 ? '99+' : String(unreadCount)) : null;

  const buyerNavItems = [
    { id: 'dashboard', to: '/buyer', icon: 'db', label: 'Dashboard', exact: true },
    { id: 'notifications', to: '/buyer/notifications', icon: 'bell', label: 'Notifications', badge: notificationBadge },
    { id: 'settings', to: '/buyer/settings', icon: 'prof', label: 'Settings' },
  ];

  const mediatorNavItems = [
    { id: 'dashboard', to: '/mediator', icon: 'db', label: 'Dashboard', exact: true },
    { id: 'notifications', to: '/mediator/notifications', icon: 'bell', label: 'Notifications', badge: notificationBadge },
    { id: 'orders', to: '/mediator/orders', icon: 'sub', label: 'Orders' },
    { id: 'my-deals', to: '/mediator/my-deals', icon: 'package', label: 'My Deals' },
    { id: 'available-deals', to: '/mediator/available-deals', icon: 'deals', label: 'Browse Deals' },
    { id: 'payouts', to: '/mediator/payouts', icon: 'paid', label: 'Rewards & Payouts' },
    { id: 'settings', to: '/mediator/settings', icon: 'settings', label: 'Settings' },
  ];

  const adminNavItems = [
    { id: 'dashboard', to: '/admin', icon: 'db', label: 'Dashboard', exact: true },
    { id: 'notifications', to: '/admin/notifications', icon: 'bell', label: 'Notifications', badge: notificationBadge },
    { id: 'orders', to: '/admin/orders', icon: 'sub', label: 'Orders' },
    { id: 'action-required', to: '/admin/action-required', icon: 'alert-triangle', label: 'Action Required' },
    { id: 'penalties', to: '/admin/penalties', icon: 'alert-circle', label: 'Penalties' },
    { id: 'users', to: '/admin/users', icon: 'prof', label: 'Users' },
    { id: 'products', to: '/admin/products', icon: 'package', label: 'Products' },
    { id: 'platforms', to: '/admin/platforms', icon: 'store', label: 'Platforms' },
    { id: 'mediators', to: '/admin/mediators', icon: 'usr', label: 'Mediators' },
    { id: 'deal-requests', to: '/admin/deal-requests', icon: 'req', label: 'Deal Requests' },
    { id: 'sheets', to: '/admin/sheets', icon: 'file', label: 'Sheets' },
    // { id: 'payouts', to: '/admin/payouts', icon: 'paid', label: 'Mediator Rewards' },
    { id: 'payouts', to: '/admin/payouts', icon: 'paid', label: 'Mediator Rewards' },
    { id: 'settings', to: '/admin/settings', icon: 'settings', label: 'Settings' },
  ];

  const sellerNavItems = [
    { id: 'dashboard', to: '/seller', icon: 'db', label: 'Dashboard', exact: true },
    { id: 'products', to: '/seller/products', icon: 'package', label: 'My Products' },
    { id: 'orders', to: '/seller/orders', icon: 'sub', label: 'Orders' },
    { id: 'analytics', to: '/seller/analytics', icon: 'trending', label: 'Analytics' },
    { id: 'settings', to: '/seller/settings', icon: 'prof', label: 'Settings' },
  ];

  const getNavItems = () => {
    if (hasRole('admin')) return adminNavItems;
    if (hasRole('mediator')) return mediatorNavItems;
    if (hasRole('seller')) return sellerNavItems;
    return buyerNavItems;
  };

  const navItems = getNavItems();

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo */}
      <div style={{ 
        padding: '20px 16px', 
        borderBottom: '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <img 
            src={geminiLogo}
            alt="ZMA Online Logo"
            style={{ 
              width: '40px', 
              height: '40px', 
              borderRadius: '10px'
            }}
          />
          <div>
            <div style={{ fontFamily: 'Outfit, sans-serif', fontWeight: '700', fontSize: '18px', color: 'white' }}>
              ZMAOnline
            </div>
            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.7)', letterSpacing: '1px', textTransform: 'uppercase' }}>
              {hasRole('admin') ? 'ADMIN PANEL' : hasRole('mediator') ? 'MEDIATOR PANEL' : 'USER PANEL'}
            </div>
          </div>
        </div>
        
        {!isDesktop && (
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '6px',
              color: 'white',
              cursor: 'pointer',
              padding: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Icon name="x" size={16} />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav style={{ flex: 1, padding: '16px', overflowY: 'auto', background: '#ffffff' }}>
        {navItems.map((item) => (
          <SidebarItem
            key={item.id}
            to={item.to}
            icon={item.icon}
            label={item.label}
            badge={item.badge}
            exact={item.exact}
            largeFont={hasRole('mediator')}
          />
        ))}
      </nav>

      {/* User info & logout */}
      <div style={{ 
        padding: '16px', 
        borderTop: '1px solid #e2e8f0',
        background: '#f8fafc'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <div 
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: '600',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              fontSize: '16px'
            }}
          >
            {user?.username?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ 
              fontSize: '14px', 
              fontWeight: '600', 
              color: '#1e293b',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {user?.username || user?.email || 'User'}
            </div>
            <div style={{ 
              fontSize: '12px', 
              color: '#64748b' 
            }}>
              {user?.role?.charAt(0)?.toUpperCase() + user?.role?.slice(1) || 'User'}
            </div>
          </div>
        </div>
        
        <button
          onClick={logout}
          style={{
            width: '100%',
            padding: '10px 16px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: 'none',
            borderRadius: '8px',
            color: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <Icon name="logout" size={16} />
          Logout
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
